import { useContext, useState } from 'react';

import { Card, Tooltip, Typography } from 'antd';

import { Icon } from '@eduzz/ui-antd-theme';

import { cn } from '@/lib/cn';
import useProductStore from '@/stores/productStore';

import { FormGroupContext } from './FormGroup';

export const Block = ({
  children,
  isValid = true,
  isEditing = false,
  lengthIndicator = undefined,
  title,
  subtitle = undefined,
  reviewWarning = false,
  warningTooltip = undefined,
  allowOverflow = false,
  allowEdit = true,
  name,
  onEdit
}: {
  children: React.ReactNode;
  isValid?: boolean;
  isEditing: boolean;
  lengthIndicator?: string;
  title: string;
  subtitle?: string;
  reviewWarning?: boolean;
  warningTooltip?: string;
  allowOverflow?: boolean;
  allowEdit?: boolean;
  name: string;
  onEdit?: (editing: boolean) => void;
}) => {
  const [isCollapsed, setIsCollapsed] = useState(false);
  const { collapsable, showValidationState, reviewMode, allowEdit: contextAllowEdit } = useContext(FormGroupContext);
  const iaGenerated = useProductStore(p => p.iaGenerated);

  return (
    <Card
      className={cn('flex-1 bg-(--uizz-antd-color-fill-tertiary)', {
        'border-ai-with-bg-(--uizz-antd-color-fill-tertiary)': iaGenerated[name]
      })}
      size='small'
    >
      <div className='flex flex-row items-center gap-2'>
        {showValidationState && !reviewMode && (
          <>
            {!isValid && (
              <div className='flex h-4 w-4 items-center justify-center rounded-full border border-gray-500'></div>
            )}
            {isValid && (
              <div className='flex h-4 w-4 items-center justify-center rounded-full border border-black bg-black font-bold text-white'>
                <Icon.Check className='h-3 w-3' />
              </div>
            )}
          </>
        )}

        <div className='flex flex-1 flex-col'>
          <div className='flex flex-row'>
            <div className='flex-1'>
              <Typography.Title className='m-0' level={5}>
                {title}
              </Typography.Title>
            </div>

            {lengthIndicator && <Typography.Text className='text-sm text-gray-400'>{lengthIndicator}</Typography.Text>}
          </div>
          {subtitle && <Typography.Text className='text-sm'>{subtitle}</Typography.Text>}
        </div>

        {collapsable && !reviewMode && (
          <div
            onClick={() => setIsCollapsed(c => !c)}
            className='inline-flex cursor-pointer items-center justify-center'
            id={`form-block-${name}-collapse-button`}
          >
            <Icon.ChevronDown
              className={cn('transition-transform duration-200 ease-in-out', isCollapsed ? 'rotate-0' : 'rotate-180')}
            />
          </div>
        )}
        {reviewMode && (
          <>
            {reviewWarning && (
              <Tooltip title={warningTooltip} trigger={'hover'}>
                <div className='flex items-center justify-center rounded-full p-0.5 text-(--uizz-antd-color-warning)'>
                  <Icon.TriangleAlert strokeWidth={2} className='h-4 w-4' />
                </div>
              </Tooltip>
            )}
            <div
              onClick={() => {
                onEdit?.(!isEditing);
              }}
              id={`form-block-${name}-edit-button`}
              className='inline-flex cursor-pointer items-center justify-center'
            >
              {allowEdit && contextAllowEdit && !isEditing && (
                <Icon.Pencil className={cn('transition-transform duration-200 ease-in-out')} />
              )}
            </div>
          </>
        )}
      </div>
      <div
        className={cn('flex flex-col gap-2 overflow-hidden transition-all duration-200', {
          'mt-0 max-h-0 scale-y-0 opacity-0 ease-out': isCollapsed,
          'mt-2 opacity-100 ease-in': !isCollapsed,
          'overflow-visible': allowOverflow,
          'text-content-body text-sm leading-5 whitespace-pre-wrap': reviewMode
        })}
      >
        {children}
      </div>
    </Card>
  );
};
